import { useAnimate, useReducedMotion } from "framer-motion";
import { CircleAlert } from "lucide-react";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import useDesign from "~/design/useDesign";
import cn from "~/utils/cn";

export interface TextFieldProps {
  input: React.InputHTMLAttributes<HTMLInputElement> & { type: string; name: string };
  label?: { text: string };
  error?: string; // translation key
  // "subtitle" (and the legacy "embedded") show the message under the field; "none" only marks the field invalid
  errorMode?: "subtitle" | "embedded" | "none" | string;
  className?: string;
  dir?: "ltr" | "rtl";
  prefix?: { text: string };
  postfix?: { text: string };
  preIcon?: React.ReactNode;
  postIcon?: React.ReactNode;
  small?: string;
  more?: () => React.ReactNode;
}

// Inset 8px with a radius concentric to the control, so the corner gap matches the side gaps.
const chip = "my-2 flex shrink-0 items-center rounded-[calc(var(--r-md)-8px)] bg-[color-mix(in_srgb,var(--c-ink)_7%,transparent)] px-3 font-display text-base text-ink-2";

// Label above, 56px control, inline error with an icon. The control follows `dir` so prefix/preIcon sit at the input's start.
function TextField({ input, label, error, errorMode = "subtitle", className, dir = "ltr", prefix, postfix, postIcon, preIcon, small, more }: TextFieldProps) {
  const { t } = useTranslation();
  const design = useDesign();
  const reduce = useReducedMotion();
  const [scope, animate] = useAnimate();
  const id = input.id ?? input.name;
  const message = error && errorMode !== "none" ? t(error) : null;
  const describedBy = message ? `${id}-error` : small ? `${id}-hint` : undefined;

  // A short shake tells the user this field is the one to fix.
  useEffect(() => {
    if (error && !reduce && scope.current) animate(scope.current, { x: [0, -6, 6, -4, 4, 0] }, { duration: 0.4 });
  }, [error, reduce, animate, scope]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      {label?.text && (
        <label htmlFor={id} className={cn("text-[15px] leading-6", design === "a" ? "font-medium text-ink-2" : "font-display font-bold text-ink")}>
          {label.text}
        </label>
      )}
      <div
        ref={scope}
        dir={dir}
        className={cn(
          "flex min-h-14 items-stretch overflow-hidden rounded-md border bg-surface transition-[border-color,box-shadow] duration-200",
          error
            ? "border-danger shadow-[0_0_0_3px_var(--c-danger-soft)]"
            : "border-field-line focus-within:border-accent-text focus-within:shadow-[inset_0_0_0_1px_var(--c-accent-text),0_0_0_4px_var(--c-accent-soft)]",
          input.disabled && "opacity-60",
        )}
      >
        {preIcon && <span className="flex shrink-0 items-center ps-4 text-ink-3 [&>svg]:size-5">{preIcon}</span>}
        {prefix && <span className={cn(chip, "ms-2")}>{prefix.text}</span>}
        <input
          dir={dir}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...input}
          id={id}
          className={cn(
            "h-14 min-w-0 flex-1 bg-transparent px-4 text-[17px] text-ink outline-none placeholder:text-ink-3 focus-visible:outline-none disabled:cursor-not-allowed",
            // Browser autofill forces an !important full-height fill that butts into the prefix chip; clipping it to the glyphs keeps the field's own surface.
            "[&:-webkit-autofill]:[background-clip:text] [&:-webkit-autofill]:[-webkit-text-fill-color:var(--c-ink)]",
            input.className,
          )}
        />
        {postfix && <span className={cn(chip, "me-2")}>{postfix.text}</span>}
        {postIcon && <span className="flex shrink-0 items-center pe-3 text-ink-3 [&>svg]:size-5">{postIcon}</span>}
      </div>
      {message && (
        <p id={`${id}-error`} role="alert" className="flex items-start gap-1.5 text-sm leading-6 text-danger">
          <CircleAlert className="mt-[3px] size-[18px] shrink-0" strokeWidth={1.75} aria-hidden />
          <span>{message}</span>
        </p>
      )}
      {!message && small && (
        <p id={`${id}-hint`} className="text-sm leading-6 text-ink-3">
          {small}
        </p>
      )}
      {more?.()}
    </div>
  );
}

export default TextField;
